/**
 * Permission System Types
 * Defines roles, permissions, and access levels for the application
 */

export type PermissionLevel = 'none' | 'read' | 'write' | 'create';

export type PageKey = 
  | 'dashboard' 
  | 'quick-select'
  | 'stream' 
  | 'emails' 
  | 'tasks' 
  | 'time-tracking'
  | 'expenses'
  | 'engagements'
  | 'team-skills'
  | 'leads' 
  | 'companies' 
  | 'opportunities' 
  | 'customers' 
  | 'products' 
  | 'offers'
  | 'documents' 
  | 'material' 
  | 'insights' 
  | 'business-plan' 
  | 'automate' 
  | 'google-ads-overview'
  | 'google-ads-campaigns'
  | 'google-ads-optimization'
  | 'google-ads-assets'
  | 'google-ads-templates'
  | 'google-ads-insights'
  | 'google-ads-merchant'
  | 'closing-assistant'
  | 'brandbook'
  | 'customer-intelligence'
  | 'company-profile'
  | 'echo-brain'
  | 'settings'
  | 'software-compliance'
  | 'development';

export interface RolePermissions {
  dashboard: PermissionLevel;
  'quick-select': PermissionLevel;
  stream: PermissionLevel;
  emails: PermissionLevel;
  tasks: PermissionLevel;
  'time-tracking': PermissionLevel;
  'expenses': PermissionLevel;
  'engagements': PermissionLevel;
  'team-skills': PermissionLevel;
  leads: PermissionLevel;
  companies: PermissionLevel;
  opportunities: PermissionLevel;
  customers: PermissionLevel;
  products: PermissionLevel;
  offers: PermissionLevel;
  documents: PermissionLevel;
  material: PermissionLevel;
  insights: PermissionLevel;
  'business-plan': PermissionLevel;
  automate: PermissionLevel;
  'google-ads-overview': PermissionLevel;
  'google-ads-campaigns': PermissionLevel;
  'google-ads-optimization': PermissionLevel;
  'google-ads-assets': PermissionLevel;
  'google-ads-templates': PermissionLevel;
  'google-ads-insights': PermissionLevel;
  'google-ads-merchant': PermissionLevel;
  'closing-assistant': PermissionLevel;
  'brandbook': PermissionLevel;
  'customer-intelligence': PermissionLevel;
  'company-profile': PermissionLevel;
  settings: PermissionLevel;
  'software-compliance': PermissionLevel;
  'development': PermissionLevel;
}

export interface Role {
  id: string;
  name: string;
  description?: string;
  isSystemRole: boolean; // true für Admin, Manager, Sales Rep
  permissions: RolePermissions;
  orgId: string;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface UserWithRole {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'manager' | 'sales_rep';
  customRoleId?: string; // Falls custom role, sonst nutzt role field
  effectivePermissions?: RolePermissions; // Berechnete Permissions
  avatar?: string;
  position?: string;
  specialties: string[];
  currentLeadCount?: number;
  orgId?: string;
  organizations?: any[];
  teamId?: string;
  status?: 'active' | 'inactive' | 'suspended';
  createdAt?: Date;
  updatedAt?: Date;
  lastActiveOrg?: string;
}

// Page metadata for UI display
export interface PageMetadata {
  key: PageKey;
  label: string;
  icon?: string;
  description?: string;
  category?: 'core' | 'sales' | 'management' | 'automation' | 'system';
}

export const PAGE_METADATA: PageMetadata[] = [
  // Core / Dashboard
  { key: 'dashboard', label: 'Dashboard', category: 'core', description: 'Main dashboard with overview' },
  { key: 'quick-select', label: 'Quick Select', category: 'core', description: 'Quick customer action menu with voice search' },
  
  // LeadHub (CRM)
  { key: 'leads', label: 'Kontakte', category: 'sales', description: 'Kontaktverwaltung' },
  { key: 'companies', label: 'Unternehmen', category: 'sales', description: 'Unternehmensverwaltung' },
  { key: 'opportunities', label: 'Verkaufschancen', category: 'sales', description: 'Sales Pipeline Management' },
  { key: 'customers', label: 'Kunden', category: 'sales', description: 'Kundenverwaltung' },
  { key: 'products', label: 'Produkte', category: 'management', description: 'Produktkatalog' },
  { key: 'offers', label: 'Angebote', category: 'sales', description: 'Angebote und Rechnungen erstellen' },
  { key: 'documents', label: 'Dokumente', category: 'sales', description: 'Angebote, Rechnungen und Verträge' },
  { key: 'closing-assistant', label: 'Abschluss', category: 'automation', description: 'KI-Abschluss-Assistent' },
  { key: 'insights', label: 'Erfolgsmessung', category: 'management', description: 'Analytics und Erfolgsmetriken' },
  
  // LeadBoost (Marketing)
  { key: 'brandbook', label: 'Brand Book', category: 'automation', description: 'Markenidentität und Wettbewerbsanalyse' },
  { key: 'google-ads-overview', label: 'Status', category: 'automation', description: 'Marketing-Dashboard mit KPIs' },
  { key: 'google-ads-campaigns', label: 'Kampagnen', category: 'automation', description: 'Google Ads Kampagnen verwalten' },
  { key: 'google-ads-optimization', label: 'Optimierung', category: 'automation', description: 'KI-gestützte Optimierungsvorschläge' },
  { key: 'google-ads-assets', label: 'Assets', category: 'automation', description: 'Asset-Bibliothek für Werbung' },
  { key: 'google-ads-templates', label: 'Vorlagen', category: 'automation', description: 'Kampagnenvorlagen' },
  { key: 'google-ads-insights', label: 'Marketing Insights', category: 'automation', description: 'Marketing Analytics und Reports' },
  { key: 'google-ads-merchant', label: 'Merchant Center', category: 'automation', description: 'Google Merchant Center Integration' },
  
  // LeadInsights (Analyse)
  { key: 'customer-intelligence', label: 'Kundenanalyse', category: 'sales', description: 'Zentrale Kunden-/Prospect-Wissensbasis' },
  
  // Company
  { key: 'company-profile', label: 'Mein Unternehmen', category: 'management', description: 'Unternehmensprofil und Einstellungen' },
  
  // Actions
  { key: 'engagements', label: 'Aufträge', category: 'management', description: 'Projekte, Managed Services, POCs und andere Aufträge' },
  { key: 'team-skills', label: 'Team', category: 'core', description: 'Team Skills und Kompetenzen' },
  { key: 'tasks', label: 'Aufgaben', category: 'core', description: 'Aufgabenverwaltung' },
  { key: 'stream', label: 'Stream', category: 'core', description: 'Aktivitäten-Feed' },
  { key: 'emails', label: 'E-Mails', category: 'sales', description: 'E-Mail-Verwaltung' },
  { key: 'time-tracking', label: 'Zeiterfassung', category: 'core', description: 'BAG-konforme Arbeitszeiterfassung' },
  { key: 'expenses', label: 'Reisekosten', category: 'core', description: 'Reisekostenabrechnung (steuerkonform)' },
  { key: 'business-plan', label: 'Business Plan', category: 'management', description: 'Geschäftsplanung' },
  { key: 'software-compliance', label: 'Software & Compliance', category: 'management', description: 'Allowlist, Nachweise, Tool-Freigaben' },
  { key: 'development', label: 'Development', category: 'management', description: 'Agile Entwicklung: Theme → Epic → User Story → Aufgabe' },
  
  // Other
  { key: 'material', label: 'Material', category: 'management', description: 'Marketing-Materialien' },
  { key: 'automate', label: 'Automatisieren', category: 'automation', description: 'Automatisierungstools' },
  { key: 'settings', label: 'Einstellungen', category: 'system', description: 'Systemeinstellungen' },
];

// Permission level ordering for comparison
export const PERMISSION_LEVELS: PermissionLevel[] = ['none', 'read', 'write', 'create'];

export function hasPermission(userLevel: PermissionLevel, requiredLevel: PermissionLevel): boolean {
  const userIndex = PERMISSION_LEVELS.indexOf(userLevel);
  const requiredIndex = PERMISSION_LEVELS.indexOf(requiredLevel);
  return userIndex >= requiredIndex;
}

export function getPermissionColor(level: PermissionLevel): string {
  switch (level) {
    case 'none': return 'bg-bg-surface text-txt-secondary border-strong';
    case 'read': return 'bg-info-soft text-info border-accent';
    case 'write': return 'bg-warning-soft text-warning border-warning';
    case 'create': return 'bg-success-soft text-success border-success';
  }
}

export function getPermissionLabel(level: PermissionLevel): string {
  switch (level) {
    case 'none': return 'No Access';
    case 'read': return 'Read Only';
    case 'write': return 'Read & Write';
    case 'create': return 'Full Access';
  }
}

